import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import {
  FormControl,
  NonNullableFormBuilder,
  Validators,
} from '@angular/forms';
import {
  CourseDTO,
  courseEntity,
  CourseFormEntity,
} from '../../shared/core/state/course/models';
import { Store, select } from '@ngrx/store';

import * as CourseActions from '../../shared/core/state/course/course.actions';
import * as ResourceActions from '../../shared/core/state/resource/resource.actions';

import { State } from '../../shared/core/state';
import { FilePondComponent } from 'ngx-filepond';
import { FilePondOptions } from 'filepond';
import { StorageService } from '../../shared/util/storage.service';
import { HttpClient } from '@angular/common/http';
import { CloudUploadService } from '../../shared/core/services/cloud-upload.service';
import {
  ResumableUploadResponse,
  UploadSession,
} from '../../shared/core/models/upload-session.model';
import { getUploadResumeURL, CONST } from '../../shared/util/const';
import { FilePondConfigService } from '../../shared/services/filepond-config.service';
import {
  firstValueFrom,
  mergeMap,
  of,
  Observable,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import {
  Resource,
  ResourceUploadStatus,
} from '../../shared/core/state/resource/models';
import CourseCreatorService from '../../shared/util/course-creator.service';
import { v4 as uuidv4 } from 'uuid';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@black/ui';
import { ResourceService } from '../../shared/core/state/resource/resource.service';
import {
  SUBJECT_OPTIONS_BY_AUDIENCE,
  TARGET_AUDIENCE_OPTIONS,
} from '../../shared/util/sri-lanka-data';

export interface LessonItem {
  id: string;
  title: string;
  resourceId?: string;
  url?: string;
  type?: string;
  isFreePreview?: boolean;
}

export interface ModuleItem {
  id: number;
  title: string;
  contents: LessonItem[];
}

@Component({
  selector: 'black-add-course',
  templateUrl: './add-course.component.html',
  styleUrls: ['./add-course.component.css'],
  standalone: false,
})
export class AddCourseComponent implements OnInit, OnDestroy {
  private fb = inject(NonNullableFormBuilder);
  private store = inject<Store<State>>(Store);
  private storageService = inject(StorageService);
  private courseCreatorService = inject(CourseCreatorService);
  private filePondConfig = inject(FilePondConfigService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toast = inject(ToastService);
  private http = inject(HttpClient);
  private uploadService = inject(CloudUploadService);
  private resourceService = inject(ResourceService);

  currentSection = 1;
  forcedSectionChange = false;
  editModeEnabled = false;
  courseStatus: string = 'DRAFT';

  get isPublished(): boolean {
    return this.courseStatus === 'PUBLISHED';
  }

  selectedCourseId$: any;
  selectedCourseId: null | string = null;
  resourceItems$!: Observable<Resource[]>;
  isSyncingContents$: any;

  targetAudienceOptions = TARGET_AUDIENCE_OPTIONS;
  subjectOptions: string[] = [];

  public ngDestroyed$ = new Subject();

  courseItems: ModuleItem[] = [];
  isMediaCollapsed = false;

  toggleMediaCollapse(): void {
    this.isMediaCollapsed = !this.isMediaCollapsed;
  }

  // Active module when adding lesson
  selectedModuleId: number | null = null;

  @ViewChild('myPond')
  myPond!: FilePondComponent;

  pondFiles: any[] = [];

  pondOptions: FilePondOptions = {
    allowMultiple: false,
    labelIdle: 'Drop files here or Browse',
    instantUpload: true,
    acceptedFileTypes: ['image/png', 'image/jpeg'],
  };

  createCourseForm = this.fb.group({
    basic: this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      summary: ['', Validators.required],
    }),
    information: this.fb.group({
      startDate: ['', Validators.required],
      medium: ['ENGLISH', Validators.required],
      targetAudience: ['', Validators.required],
      targetAudienceOther: [''],
      subject: ['', Validators.required],
      subjectOther: [''],
      requirements: this.fb.array([this.fb.control('')]),
      coverage: this.fb.array([this.fb.control('')]),
      totalDurationHour: [0, Validators.max(120)],
      totalDurationMinutes: [0, Validators.max(60)],
      tags: [''],
    }),
    settings: this.fb.group({
      maxStudents: [0],
      isPublic: [true],
      price: [0],
      banner: [''],
      installmentOptions: this.fb.group({
        enabled: [false],
        numberOfInstallments: [2],
        intervalDays: [30],
      }),
    }),
  });

  // Modal controls
  topicTitleControl = new FormControl('', { nonNullable: true });

  // Lesson controls
  lessonTitleControl = new FormControl('', { nonNullable: true });
  lessonTypeControl = new FormControl<string>('video', { nonNullable: true });
  lessonSourceMode: 'file' | 'url' | 'library' | 'none' = 'file';
  lessonUrlControl = new FormControl('', { nonNullable: true });
  lessonResourceControl = new FormControl('', { nonNullable: true });
  lessonFreePreviewControl = new FormControl<boolean>(false, {
    nonNullable: true,
  });

  // Direct file upload in lesson modal
  lessonSelectedFile: File | null = null;
  lessonUploadSession: UploadSession | null = null;
  lessonUploadSessionId: number | null = null;
  isLessonUploading = false;
  lessonUploadSuccess = false;
  private lessonUploadSub?: Subscription;

  formControls = {
    basic: this.createCourseForm.controls.basic.controls,
    information: this.createCourseForm.controls.information.controls,
    settings: this.createCourseForm.controls.settings.controls,
  };

  constructor() {
    this.isSyncingContents$ = this.store.select(
      (store) => store.courses.isSyncingContents,
    );

    this.selectedCourseId$ = this.store.select(
      (store) => store.courses.createdCourseItem?._id,
    );

    this.resourceItems$ = this.store.pipe(
      select((store) => store.resources.resourceItems),
      mergeMap((items) =>
        of(
          items.filter(
            (item) => item.uploadStatus == ResourceUploadStatus.COMPLETED,
          ),
        ),
      ),
    );
  }

  ngOnDestroy(): void {
    if (this.isLessonUploading && this.lessonUploadSessionId !== null) {
      this.uploadService.cancelUpload(this.lessonUploadSessionId);
    }
    this.lessonUploadSub?.unsubscribe();
    this.syncCourseContentData();
    this.ngDestroyed$.next('');
    this.ngDestroyed$.complete();
  }

  async ngOnInit(): Promise<void> {
    const server = await this.filePondConfig.getServerConfig({
      allowRevert: true,
    });
    this.pondOptions = { ...this.pondOptions, server };

    const isEditMode = !!this.route.snapshot.data['editMode'];
    const paramCourseId =
      this.route.snapshot.params['id'] || this.route.snapshot.queryParams['id'];

    if (!isEditMode) {
      // Clean slate for new course creation
      this.editModeEnabled = false;
      this.currentSection = 1;
      this.store.dispatch(CourseActions.clearCreatedCourseItem());
      this.storageService.remove(CONST.STORAGE_KEYS.CREATE_COURSE_ID);
      await this.courseCreatorService.flushData();
      this.createCourseForm.reset({
        basic: { title: '', description: '', summary: '' },
        information: {
          startDate: '',
          medium: 'ENGLISH',
          targetAudience: '',
          targetAudienceOther: '',
          subject: '',
          subjectOther: '',
          requirements: [''],
          coverage: [''],
          totalDurationHour: 0,
          totalDurationMinutes: 0,
          tags: '',
        },
        settings: {
          maxStudents: 0,
          isPublic: true,
          price: 0,
          banner: '',
          installmentOptions: {
            enabled: false,
            numberOfInstallments: 2,
            intervalDays: 30,
          },
        },
      });
      this.pondFiles = [];
      this.courseItems = [];
    } else {
      // Edit mode: fetch course by route param, query param, or stored ID
      if (paramCourseId) {
        this.storageService.set(
          CONST.STORAGE_KEYS.CREATE_COURSE_ID,
          paramCourseId,
        );
        this.store.dispatch(
          CourseActions.restoreCreateCourseItem({ courseId: paramCourseId }),
        );
      } else {
        this.storageService
          .get(CONST.STORAGE_KEYS.CREATE_COURSE_ID)
          .then((courseId) => {
            if (courseId) {
              this.store.dispatch(
                CourseActions.restoreCreateCourseItem({ courseId }),
              );
            }
          });
      }
    }

    this.store.dispatch(CourseActions.initiateCourseCreation());

    // Audience change listener to dynamically set available subjects
    this.createCourseForm.controls.information.controls.targetAudience.valueChanges
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe((audience) => {
        this.subjectOptions = SUBJECT_OPTIONS_BY_AUDIENCE[audience] || [];
        if (
          !this.subjectOptions.includes(
            this.createCourseForm.controls.information.controls.subject.value,
          )
        ) {
          this.createCourseForm.controls.information.controls.subject.setValue(
            '',
          );
        }
      });

    this.store
      .select((state) => state.courses.createdCourseItem)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(async (courseItem) => {
        if (courseItem) {
          this.editModeEnabled = true;
          this.courseStatus = (courseItem.status || 'DRAFT').toUpperCase();

          // store courseId for continuation
          this.storageService.set(
            CONST.STORAGE_KEYS.CREATE_COURSE_ID,
            courseItem._id,
          );

          const fetchedCourseFormData = new courseEntity(courseItem);

          // Update audience options
          if (fetchedCourseFormData.information.targetAudience) {
            this.subjectOptions =
              SUBJECT_OPTIONS_BY_AUDIENCE[
                fetchedCourseFormData.information.targetAudience
              ] || [];
          }

          // auto extend controls based on data
          this.patchFormControls(fetchedCourseFormData);

          this.createCourseForm.patchValue(fetchedCourseFormData);
          const bannerResource = courseItem.settings?.bannerImageResource;
          if (bannerResource) {
            this.pondFiles.push({
              source: bannerResource._id,
              options: {
                type: 'limbo',
                file: {
                  name: bannerResource.name,
                  size: 3001025,
                  type: 'image/png',
                },
              },
            });
          }

          if (!this.forcedSectionChange) {
            this.currentSection = 2;
          }

          this.fetchCourseResources(courseItem._id);
          this.courseCreatorService.setCourseId(courseItem._id);

          const courseItemsSnap = (courseItem.contents || []).map((content) => {
            return {
              title: content.topic,
              contents: (content.resources || []).map((resource) => {
                return {
                  id: resource._id,
                  title: resource.title,
                  resourceId: resource.resource,
                  url: resource.url,
                  type: resource.type || 'video',
                  isFreePreview: resource.isFreePreview || false,
                };
              }),
            };
          });

          const conflictExist: boolean =
            await this.courseCreatorService.checkConflict(courseItemsSnap);

          if (conflictExist) {
            await this.courseCreatorService.flushData();
            await this.courseCreatorService.pullFromState(courseItemsSnap);
          }

          this.courseCreatorService
            .getCourseItemsByCourseId(courseItem._id)
            .subscribe((items) => {
              this.courseItems = [];
              items.forEach((item) => {
                this.courseItems.push({
                  id: item.id ?? 0,
                  title: item.title,
                  contents: (item.contents || []).map((c) => {
                    return {
                      id: c.id,
                      title: c.title,
                      resourceId: c.resourceId,
                      url: c.url,
                      type: c.type || 'video',
                      isFreePreview: c.isFreePreview || false,
                    };
                  }),
                });
              });
            });
        } else {
          this.currentSection = 1;
        }
      });

    this.selectedCourseId$.subscribe((id: string | undefined) => {
      if (id) this.selectedCourseId = id ?? null;
    });
  }

  get installmentPreview(): string {
    const {
      price,
      installmentOptions: { numberOfInstallments: num, intervalDays: days },
    } = this.createCourseForm.controls.settings.getRawValue();
    return price > 0 && num > 1
      ? `${num} installments of LKR ${Math.floor(price / num).toLocaleString('en-LK')} every ${days} days`
      : '';
  }

  changeCurrentSection() {
    this.currentSection = this.currentSection === 1 ? 2 : 1;
  }

  get requirementsFormControls() {
    return this.createCourseForm.controls.information.controls.requirements
      .controls;
  }

  get coverageFormControls() {
    return this.createCourseForm.controls.information.controls.coverage
      .controls;
  }

  addMoreRequirementInputs() {
    this.createCourseForm.controls.information.controls.requirements.push(
      this.fb.control(''),
    );
  }

  removeRequirementInput(index: number) {
    this.createCourseForm.controls.information.controls.requirements.removeAt(
      index,
    );
  }

  addMoreCoverageInputs() {
    this.createCourseForm.controls.information.controls.coverage.push(
      this.fb.control(''),
    );
  }

  removeCoverageInput(index: number) {
    this.createCourseForm.controls.information.controls.coverage.removeAt(
      index,
    );
  }

  patchFormControls(fetchedCourseFormData: courseEntity) {
    const requirementFormControlLength =
      this.formControls.information.requirements.controls.length;
    const coverageFormControlLength =
      this.formControls.information.coverage.controls.length;

    const requirementFormLength = (
      fetchedCourseFormData.information.requirements || []
    ).length;
    const coverageFormLength = (
      fetchedCourseFormData.information.coverage || []
    ).length;

    for (
      let index = 0;
      index < requirementFormLength - requirementFormControlLength;
      index++
    ) {
      this.formControls.information.requirements.push(this.fb.control(''));
    }

    for (
      let index = 0;
      index < coverageFormLength - coverageFormControlLength;
      index++
    ) {
      this.formControls.information.coverage.push(this.fb.control(''));
    }

    if (coverageFormLength < coverageFormControlLength) {
      const diff = coverageFormControlLength - coverageFormLength;
      for (let index = 0; index < diff; index++) {
        this.formControls.information.coverage.controls.pop();
      }
    }

    if (requirementFormLength < requirementFormControlLength) {
      const diff = requirementFormControlLength - requirementFormLength;
      for (let index = 0; index < diff; index++) {
        this.formControls.information.requirements.controls.pop();
      }
    }
  }

  submit() {
    this.createCourseForm.markAllAsTouched();

    if (this.createCourseForm.valid) {
      const courseDTO = new CourseDTO(
        this.createCourseForm.value as CourseFormEntity,
      );

      if (this.editModeEnabled) {
        this.storageService
          .get(CONST.STORAGE_KEYS.CREATE_COURSE_ID)
          .then((courseId) => {
            if (courseId) {
              this.store.dispatch(
                CourseActions.updateCourseItem({
                  courseId,
                  courseItemDTO: courseDTO,
                }),
              );
            }
          });
      } else {
        this.store.dispatch(
          CourseActions.createCourseItem({ courseItemDTO: courseDTO }),
        );
      }
    } else {
      this.toast.showToast(
        'Please fill all required fields correctly!',
        'error',
      );
    }
  }

  // --- Curriculum & LMS Builder Methods ---

  addTopic(callback: () => void) {
    const title = this.topicTitleControl.value.trim();
    if (title) {
      this.courseCreatorService.addCourseItem({
        title,
        contents: [],
      });
    }
    this.topicTitleControl.reset();
    callback();
  }

  deleteTopic(id: number) {
    this.courseCreatorService.deleteCourseItem(id);
  }

  setLessonSourceMode(mode: 'file' | 'url' | 'library' | 'none') {
    this.lessonSourceMode = mode;
  }

  async onLessonFilePicked(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    this.lessonSelectedFile = file;
    this.isLessonUploading = true;
    this.lessonUploadSuccess = false;
    this.lessonUploadSession = null;
    this.lessonUploadSub?.unsubscribe();

    // Auto-fill title if empty
    if (!this.lessonTitleControl.value.trim()) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      this.lessonTitleControl.setValue(nameWithoutExt);
    }

    // Auto-detect type
    if (file.type.startsWith('video/')) {
      this.lessonTypeControl.setValue('video');
    } else if (
      file.type.includes('pdf') ||
      file.type.includes('document') ||
      file.name.endsWith('.pdf')
    ) {
      this.lessonTypeControl.setValue('document');
    }

    try {
      const data = await firstValueFrom(
        this.http.post<ResumableUploadResponse>(getUploadResumeURL(), {
          name: file.name,
          type: file.type || 'application/octet-stream',
          size: file.size,
          courseId: this.selectedCourseId || undefined,
        }),
      );

      if (data && data.resourceId) {
        const uploadSessionId = this.uploadService.addUploadFromResponse(
          file,
          data,
        );
        this.lessonUploadSessionId = uploadSessionId;
        this.lessonResourceControl.setValue(data.resourceId);

        this.lessonUploadSub = this.uploadService.uploads$
          .pipe(takeUntil(this.ngDestroyed$))
          .subscribe((sessions) => {
            const session = sessions.find((s) => s.id === uploadSessionId);
            if (!session) return;
            this.lessonUploadSession = session;
            if (session.uploadStatus === 'completed') {
              this.isLessonUploading = false;
              this.lessonUploadSuccess = true;
              this.toast.showToast(
                'File uploaded and linked to lesson!',
                'success',
              );
            } else if (session.uploadStatus === 'failed') {
              this.isLessonUploading = false;
              this.lessonUploadSuccess = false;
              this.toast.showToast(
                'File upload failed. You can retry or pick another file.',
                'error',
              );
            } else if (session.uploadStatus === 'cancelled') {
              this.isLessonUploading = false;
              this.lessonUploadSuccess = false;
            } else {
              this.isLessonUploading = true;
              this.lessonUploadSuccess = false;
            }
          });
      } else {
        throw new Error('Upload failed to initialize');
      }
    } catch (err) {
      this.isLessonUploading = false;
      this.toast.showToast(
        'File upload failed. You can try again or link an external URL.',
        'error',
      );
    }
  }

  removeLessonFile() {
    if (this.lessonUploadSessionId !== null) {
      this.uploadService.cancelUpload(this.lessonUploadSessionId);
      this.uploadService.removeUpload(this.lessonUploadSessionId);
    }
    this.lessonUploadSub?.unsubscribe();
    this.lessonUploadSub = undefined;
    this.lessonUploadSession = null;
    this.lessonUploadSessionId = null;
    this.lessonSelectedFile = null;
    this.lessonResourceControl.reset();
    this.lessonUploadSuccess = false;
    this.isLessonUploading = false;
  }

  userExplicitlySetType = false;

  setLessonType(type: string) {
    this.lessonTypeControl.setValue(type);
    this.userExplicitlySetType = true;
  }

  onLessonUrlInput() {
    if (this.userExplicitlySetType) return;
    const url = (this.lessonUrlControl.value || '').trim();
    if (!url) return;
    const isDoc = /\.pdf($|[?#])/i.test(url);
    const isVid = /youtu|vimeo|drive\.google|\.(mp4|webm|ogg|mov|m4v|mkv|m3u8|mpd)($|[?#])/i.test(url);
    this.lessonTypeControl.setValue(isDoc ? 'document' : isVid ? 'video' : 'link');
  }

  openAddLesson(moduleId: number, modalToggle: () => void) {
    this.selectedModuleId = moduleId;
    this.lessonTitleControl.reset();
    this.lessonTypeControl.setValue('video');
    this.userExplicitlySetType = false;
    this.lessonSourceMode = 'file';
    this.lessonUrlControl.reset();
    this.lessonResourceControl.reset();
    this.lessonFreePreviewControl.setValue(false);
    this.removeLessonFile();
    modalToggle();
  }

  addLesson(closeModal: () => void) {
    if (!this.selectedModuleId) return;

    if (
      this.lessonSourceMode === 'file' &&
      this.lessonSelectedFile &&
      !this.lessonUploadSuccess
    ) {
      this.toast.showToast(
        'Please wait for the file to finish uploading before adding the lesson.',
        'warning',
      );
      return;
    }

    const title = this.lessonTitleControl.value.trim();
    if (!title) {
      this.toast.showToast('Lesson title is required', 'error');
      return;
    }

    const type = this.lessonTypeControl.value;
    const isFreePreview = this.lessonFreePreviewControl.value;

    let resourceId: string | undefined = undefined;
    let url: string | undefined = undefined;

    if (this.lessonSourceMode === 'file') {
      resourceId = this.lessonResourceControl.value || undefined;
    } else if (this.lessonSourceMode === 'url') {
      url = this.lessonUrlControl.value.trim() || undefined;
      if (url) {
        const moduleId = this.selectedModuleId;
        const lessonId = uuidv4();
        this.isLessonUploading = true;
        this.resourceService
          .uploadExternalUrl({
            name: title,
            url,
            type,
            courseId: this.selectedCourseId || undefined,
          })
          .subscribe({
            next: ({ resourceId: extResourceId }) => {
              this.isLessonUploading = false;
              this.courseCreatorService.updateCourseContent(moduleId, {
                id: lessonId,
                title,
                resourceId: extResourceId,
                url,
                type,
                isFreePreview,
              });
              this.toast.showToast('External link added', 'success');
              closeModal();
            },
            error: () => {
              this.isLessonUploading = false;
              this.courseCreatorService.updateCourseContent(moduleId, {
                id: lessonId,
                title,
                url,
                type,
                isFreePreview,
              });
              closeModal();
            },
          });
        return;
      }
    } else if (this.lessonSourceMode === 'library') {
      resourceId = this.lessonResourceControl.value || undefined;
    }

    this.courseCreatorService.updateCourseContent(this.selectedModuleId, {
      id: uuidv4(),
      title,
      resourceId,
      url,
      type,
      isFreePreview,
    });

    closeModal();
  }

  deleteResource(courseItemId: number, courseContentId: string) {
    this.courseCreatorService.removeCourseContent(
      courseItemId,
      courseContentId,
    );
  }

  toggleFreePreview(courseItemId: number, lesson: LessonItem) {
    this.courseCreatorService.updateCourseContent(courseItemId, {
      ...lesson,
      isFreePreview: !lesson.isFreePreview,
    });
  }

  syncCourseContentData() {
    this.courseCreatorService.pushToServer();
    this.toast.showToast(
      this.isPublished ? 'Course changes saved' : 'Curriculum draft saved',
      'success',
    );
  }

  syncAndExit() {
    this.courseCreatorService.pushToServer();
    this.store
      .select((store) => store.courses.isSyncSuccess)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe((isSuccess) => {
        if (isSuccess === true) {
          if (this.isPublished) {
            this.toast.showToast('Course changes saved successfully', 'success');
            this.router.navigate(['/dashboard/courses']);
          } else {
            this.router.navigate([
              '/courses/add-success/' + this.selectedCourseId,
            ]);
          }
        }
      });
  }

  pondHandleProcessFile(event: any) {
    if (!event.error) {
      this.createCourseForm.controls.settings.controls.banner.setValue(
        event.file.serverId,
      );
    }
  }

  pondHandleRevertFile(event: any) {
    this.createCourseForm.controls.settings.controls.banner.setValue('');
  }

  fetchCourseResources(courseId: string) {
    if (courseId && courseId !== '') {
      this.store.dispatch(
        ResourceActions.fetchResourceItemsByCourse({
          courseId,
        }),
      );
    }
  }

  getTotalLessons(): number {
    return this.courseItems.reduce(
      (acc, m) => acc + (m.contents?.length || 0),
      0,
    );
  }
}
